import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "@/config/env";

/** Supabase social login is available only when the project keys are configured. */
export const isSupabaseConfigured = Boolean(
  SUPABASE_URL.trim() && SUPABASE_ANON_KEY.trim(),
);

/**
 * Single shared Supabase client. `null` when env keys are missing so the UI can
 * disable the social buttons instead of throwing at import time.
 *
 * `detectSessionInUrl` lets the client finish the OAuth handshake automatically
 * when the provider redirects back to `/auth/callback`.
 */
export const supabase: SupabaseClient | null = isSupabaseConfigured
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        flowType: "pkce",
      },
    })
  : null;

export type SupabaseOAuthProvider = "google" | "kakao";

/** Path the OAuth provider redirects back to after authenticating. */
export const SUPABASE_OAUTH_CALLBACK_PATH = "/auth/callback";

/** Survives the full-page OAuth redirect so we can return the user where they started. */
export const SUPABASE_OAUTH_REDIRECT_KEY = "supabaseOAuthRedirect";

/** Survives the redirect so the callback knows which provider's endpoint to call. */
export const SUPABASE_OAUTH_PROVIDER_KEY = "supabaseOAuthProvider";

/** Suffix Supabase appends to its storage key for the stored PKCE code verifier. */
const SUPABASE_CODE_VERIFIER_SUFFIX = "-auth-token-code-verifier";

/**
 * Read the PKCE code verifier Supabase stored before the OAuth redirect
 * (key: `sb-<project-ref>-auth-token-code-verifier`). The backend needs it to
 * exchange the `code` for a session. `remove` clears it once consumed.
 */
export function getSupabaseCodeVerifier(remove = true): string | null {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.endsWith(SUPABASE_CODE_VERIFIER_SUFFIX)) {
        const raw = localStorage.getItem(key);
        if (remove) {
          localStorage.removeItem(key);
        }
        if (!raw) return null;
        // Value can be JSON-quoted and/or `verifier/redirectType`; keep the verifier.
        const cleaned = raw.replace(/^"|"$/g, "");
        return cleaned.split("/")[0] || null;
      }
    }
  } catch {
    // localStorage may be unavailable (privacy mode); fail soft.
  }
  return null;
}

/**
 * Kick off a provider OAuth redirect through Supabase. Stores the post-login
 * destination first because React Router `state` is lost across the redirect.
 */
export async function startSupabaseOAuth(
  provider: SupabaseOAuthProvider,
  redirectPath?: string,
): Promise<void> {
  if (!supabase) {
    throw new Error(
      "Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.",
    );
  }

  if (redirectPath) {
    sessionStorage.setItem(SUPABASE_OAUTH_REDIRECT_KEY, redirectPath);
  }
  sessionStorage.setItem(SUPABASE_OAUTH_PROVIDER_KEY, provider);

  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}${SUPABASE_OAUTH_CALLBACK_PATH}`,
      // Kakao needs explicit scopes to return the email + nickname.
      ...(provider === "kakao"
        ? { scopes: "account_email profile_nickname profile_image" }
        : {}),
    },
  });

  if (error) {
    throw error;
  }
}
